import { useMemo } from "react";
import { HubTradePrepareOrderBody, MarketRate, OrderType, PreparedOrder, TradeForm } from "@cede/types";
import { API_ENDPOINT, CEDE_CEXS, getCurrentRate, Precise, unformatNumberAsString } from "@cede/utils";
import { useMultipleTransactionsFees } from "../multi-transaction/useMultipleTransactionFees";
import { useApi } from "@/hooks/core/useApi";
import { usePrice } from "@/hooks/prices";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

type UseTradeFeeParams = {
  tradeForm: TradeForm;
  marketRate: MarketRate;
  shouldUseTradeSquare: boolean;
  debouncedAmountFrom: string | null;
  orderType: OrderType;
};

export const useTradeFee = ({
  tradeForm,
  marketRate,
  shouldUseTradeSquare,
  debouncedAmountFrom,
  orderType,
}: UseTradeFeeParams) => {
  const apiHelper = useApi();
  const { auth, hubUser } = useHubUser();
  const tokenFromPrice = usePrice(tradeForm.tokenFrom?.value || "", tradeForm.selectedAccount?.cexValue);

  const { bid: marketBid = "0", ask: marketAsk = "0" } = marketRate || {};
  const marketRateKey = useMemo(() => `${marketBid}-${marketAsk}`, [marketBid, marketAsk]);

  const { data: preparedOrderWithFeeForCommonMarket, isFetching: preparedOrderWithFeeForCommonMarketIsLoading } =
    useQuery({
      queryKey: [
        "tradeFee",
        tradeForm.orderSide,
        debouncedAmountFrom,
        tradeForm.market?.pairSymbol,
        marketRateKey,
        auth.identityToken,
      ],
      queryFn: async () => {
        if (!auth.identityToken || !hubUser) return null;

        const orderSide = tradeForm.orderSide;
        if (!orderSide) return null;

        const currentRate =
          orderType === OrderType.MARKET
            ? getCurrentRate(marketRate, orderSide)
            : unformatNumberAsString(tradeForm.limitPrice).toString();
        const unformattedAmountFrom = String(unformatNumberAsString(debouncedAmountFrom || "0"));
        const computedBaseAmount = Precise.stringDiv(unformattedAmountFrom, currentRate);
        const baseAmount = orderSide === "sell" ? unformattedAmountFrom : (computedBaseAmount ?? "0");

        const account = hubUser.exchangeAccounts.find((acc) => acc.id === Number(tradeForm.selectedAccount?.value));
        if (!account) return null;

        const pairSymbol = tradeForm.market?.pairSymbol;
        if (!pairSymbol) return null;

        const requestBody: HubTradePrepareOrderBody = {
          accountId: Number(tradeForm.selectedAccount?.value),
          pairSymbol,
          orderSide,
          amount: baseAmount,
          orderType,
          price: currentRate,
          exchangeId: account.exchangeId,
          // Pass original quote amount for MEXC buy orders to preserve precision
          ...(account.exchangeId === CEDE_CEXS.MEXC &&
            orderSide === "buy" &&
            orderType === OrderType.MARKET && {
              quoteAmount: unformattedAmountFrom,
            }),
        };

        const preparedOrder: PreparedOrder = await apiHelper.postAuth(auth, API_ENDPOINT.HUB_TRADE_PREPARE_ORDER, {
          bodyParams: requestBody,
        });

        const fee = {
          tokenSymbol: preparedOrder?.estimatedFee?.tokenSymbol || "",
          amount: preparedOrder?.estimatedFee?.amount || 0,
          refAmount: preparedOrder?.estimatedFee?.refAmount || 0,
        };
        return {
          fee,
          preparedOrder,
        };
      },
      enabled:
        !!auth.identityToken &&
        !!hubUser &&
        !!tradeForm.selectedAccount &&
        !!tradeForm.orderSide &&
        !!tradeForm.market &&
        !!debouncedAmountFrom &&
        tradeForm.amountFrom !== "0" &&
        tradeForm.amountTo !== "0",
      staleTime: 1_000,
    });

  const { multipleTransactionFeesInFromToken, isLoading: isMultipleTransactionsFeesLoading } =
    useMultipleTransactionsFees({
      enabled: shouldUseTradeSquare,
      accountId: tradeForm.selectedAccount?.value || "",
      fromToken: tradeForm.tokenFrom?.value || "",
      toToken: tradeForm.tokenTo?.value || "",
      fromTokenAmount: debouncedAmountFrom || "",
    });

  const multipleTransactionFees = useMemo(() => {
    return {
      tokenSymbol: tradeForm.tokenFrom?.value || "",
      amount: Number(multipleTransactionFeesInFromToken),
      refAmount: tokenFromPrice * Number(multipleTransactionFeesInFromToken),
    };
  }, [multipleTransactionFeesInFromToken, tokenFromPrice]);

  return {
    fee: shouldUseTradeSquare ? multipleTransactionFees : preparedOrderWithFeeForCommonMarket?.fee,
    preparedOrder: preparedOrderWithFeeForCommonMarket?.preparedOrder,
    isLoading: shouldUseTradeSquare ? isMultipleTransactionsFeesLoading : preparedOrderWithFeeForCommonMarketIsLoading,
  };
};
